import { Link } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import {
  EDUCATION_LEVELS,
  PIPELINE_COLUMNS,
  PIPELINE_COLUMN_LABELS,
  PIPELINE_FLAG_LABELS,
  PIPELINE_STAGE_LABELS,
  type ApplicationStage,
  type EducationLevel,
  type PipelineColumn,
  type PipelineFlag,
  type PipelineRow,
} from '@nest-recruit/shared';
import { Chip } from '../../../components/Chip';
import type { Column } from '../../../components/data-table/types';
import { formatDate } from '../../../lib/format';
import { educationLabel, experienceLabel } from '../../candidates/candidate-format';
import styles from './PipelineList.module.css';

const MS_PER_DAY = 86_400_000;

export interface ColumnContext {
  readonly labels: ReadonlyMap<ApplicationStage, string>;
  /** Rows whose change is on its way to the server — marked on that row alone (§5a rule 4). */
  readonly pending: ReadonlySet<string>;
  readonly now: number;
  /** R1 (§L34): days in stage is a Time figure, shown only while that module is on. */
  readonly isDaysShown: boolean;
}

/** The sortable ones map straight onto the API's `sort` (06-qol §3, multi-column with shift). */
const SORTABLE: ReadonlySet<PipelineColumn> = new Set(['candidate', 'stage', 'score', 'days', 'applied', 'source', 'recruiter']);

const WIDTHS: Readonly<Partial<Record<PipelineColumn, number>>> = {
  candidate: 260,
  stage: 150,
  score: 90,
  days: 120,
  applied: 120,
  source: 140,
  recruiter: 160,
  tags: 200,
  flags: 220,
  email: 220,
  mobile: 160,
  reference: 150,
};

/** SCR-02's columns, in the order DECISIONS-LOG S16 Q7 chose. The hidden ones are shown from the column menu. */
export function pipelineColumns(context: ColumnContext): readonly Column<PipelineRow>[] {
  const cells: Readonly<Record<PipelineColumn, (row: PipelineRow) => ReactNode>> = {
    candidate: (row) => <CandidateCell row={row} />,
    stage: (row) => (
      <span className={styles.stack}>
        <span>{context.labels.get(row.stage) ?? PIPELINE_STAGE_LABELS[row.stage]}</span>
        {context.pending.has(row.id) && <span className={styles.saving}>Saving…</span>}
      </span>
    ),
    score: (row) => row.matchScore,
    days: (row) => daysLabel(Math.max(0, Math.floor((context.now - new Date(row.stageEnteredAt).getTime()) / MS_PER_DAY))),
    applied: (row) => formatDate(row.appliedAt),
    source: (row) => row.sourceLabel,
    recruiter: (row) => row.recruiterName,
    tags: (row) =>
      row.tags.length === 0 ? null : (
        <span className={styles.chips}>
          {row.tags.map((tag) => (
            <Chip key={tag.id} tone="neutral" plain>
              {tag.name}
            </Chip>
          ))}
        </span>
      ),
    flags: (row) => {
      const flags = flagsOf(row);
      return flags.length === 0 ? null : (
        <span className={styles.chips}>
          {flags.map((flag) => (
            <Chip key={flag} tone={flag === 'knockout' || flag === 'missing_must_have' ? 'warning' : 'neutral'} plain>
              {PIPELINE_FLAG_LABELS[flag]}
            </Chip>
          ))}
        </span>
      );
    },
    email: (row) => row.candidate.email,
    mobile: (row) => row.candidate.phone,
    city: (row) => row.candidate.city,
    experience: (row) => experienceLabel(row.candidate.totalExperienceMonths),
    education: (row) => educationLabel(educationOf(row.candidate.highestEducation)),
    expectedSalary: (row) => row.candidate.expectedSalary,
    noticePeriod: (row) => (row.candidate.noticePeriodDays === null ? null : daysLabel(row.candidate.noticePeriodDays)),
    reference: (row) => <span className={styles.mono}>{row.reference}</span>,
  };
  return PIPELINE_COLUMNS.filter((id) => id !== 'days' || context.isDaysShown).map((id) => ({
    id,
    // ADR 0062: the number is the ideal candidate match, not a score.
    header: id === 'score' ? 'Match' : PIPELINE_COLUMN_LABELS[id],
    cell: cells[id],
    isSortable: SORTABLE.has(id),
    isNumeric: id === 'score',
    isIdentity: id === 'candidate',
    ...(WIDTHS[id] ? { defaultWidth: WIDTHS[id] } : {}),
  }));
}

function CandidateCell({ row }: { readonly row: PipelineRow }): ReactNode {
  const role = [row.candidate.currentTitle, row.candidate.currentEmployer].filter(Boolean).join(' · ');
  return (
    <span className={styles.stack}>
      <span className={styles.nameLine}>
        <Link to="/applications/$applicationId" params={{ applicationId: row.id }} className={styles.name} onClick={(event) => event.stopPropagation()}>
          {row.candidate.fullName}
        </Link>
        {row.candidate.doNotRehire && (
          <Chip tone="danger" plain>
            Do not rehire
          </Chip>
        )}
        {row.lockedByName && (
          <Chip tone="progress" plain>
            {row.lockedByName} is sorting
          </Chip>
        )}
      </span>
      {role !== '' && <span className={styles.meta}>{role}</span>}
    </span>
  );
}

export function flagsOf(row: PipelineRow): readonly PipelineFlag[] {
  const flags: PipelineFlag[] = [];
  if (row.flags.isKnockoutFlagged) flags.push('knockout');
  if (row.flags.isMissingMustHave) flags.push('missing_must_have');
  if (row.flags.isReapplicant) flags.push('reapplicant');
  if (row.flags.isReferred) flags.push('referred');
  if (row.flags.isActiveElsewhere) flags.push('elsewhere');
  if (row.flags.isPooled) flags.push('pooled');
  return flags;
}

function daysLabel(days: number): string {
  return days === 1 ? '1 day' : `${days} days`;
}

function educationOf(value: string | null): EducationLevel | null {
  return EDUCATION_LEVELS.find((level) => level === value) ?? null;
}
